import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { washAppointmentStatusSchema } from "./enums.js";
/**
 * El yıkama randevusu (Prompt 18, ADR 0018 §12). Uygunluk KAPASİTE bazlıdır: randevu, süresi boyunca her anda örtüşen
 * (BOOKED + CHECKED_IN) randevu sayısı < ünite kapasitesi ise kabul edilir. Süre = seçilen HAND_WASH programlarının
 * süreleri toplamı, dakikaya YUKARI yuvarlanır. Çalışma saatleri dışı → `422 WASH_OUTSIDE_HOURS`, dolu → `409
 * WASH_SLOT_FULL`.
 *
 * KVKK: `plate`, `contactName`, `contactPhone`, `note` kişisel veridir. Çağıranın randevunun istasyonunda `wash.operate`
 * (görevli aracı ve müşteriyi tanımalıdır) ya da `customers.personal.view` izni yoksa MASKELİ döner
 * (`personalDataMasked: true`).
 */
const personal = (description) => z.string().nullable().describe(`KVKK: ${description} (maskeli olabilir)`);
const optionalVersion = z
    .number()
    .int()
    .min(1)
    .optional()
    .describe("İyimser kilit sürümü (verilirse denetlenir)");
/** Randevu / iş emri satırındaki hizmet (HAND_WASH programı). */
export const washServiceProgramSchema = z.object({
    programId: uuidSchema,
    code: z.string(),
    name: z.string(),
    durationSeconds: z.number().int().nullable(),
});
export const washAppointmentSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    unitId: uuidSchema,
    unitName: z.string(),
    startsAt: timestampSchema,
    endsAt: timestampSchema,
    durationMinutes: z.number().int(),
    status: washAppointmentStatusSchema,
    programs: z.array(washServiceProgramSchema),
    plate: personal("araç plakası (normalleştirilmiş)"),
    customerProfileId: uuidSchema.nullable(),
    contactName: personal("ad soyad"),
    contactPhone: personal("telefon (E.164)"),
    note: personal("not"),
    workOrderId: uuidSchema.nullable().describe("Geldi (check-in) ile açılan iş emri"),
    workOrderNo: z.string().nullable(),
    cancelReason: z.string().nullable(),
    personalDataErasedAt: timestampSchema
        .nullable()
        .describe("KVKK anonimleştirmesi (kişisel alanlar boşaltıldı)"),
    personalDataMasked: z.boolean(),
    allowedTransitions: z.array(washAppointmentStatusSchema),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const washAppointmentPageSchema = pageOf(washAppointmentSchema);
export const listWashAppointmentsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    from: localDateSchema.optional().describe("İlk gün (İstanbul, dahil)"),
    to: localDateSchema.optional().describe("Son gün (İstanbul, dahil)"),
    unitId: uuidSchema.optional(),
    status: washAppointmentStatusSchema.optional(),
});
const programIdsSchema = z
    .array(uuidSchema)
    .min(1)
    .max(10)
    .refine((ids) => new Set(ids).size === ids.length, "Aynı hizmet iki kez seçilemez");
export const createWashAppointmentSchema = z
    .object({
    unitId: uuidSchema.describe("El yıkama (HAND_WASH) ünitesi"),
    startsAt: timestampSchema.describe("Başlangıç (dakika hassasiyetinde)"),
    programIds: programIdsSchema.describe("HAND_WASH programları (ünitede tanımlı, etkin)"),
    plate: z.string().trim().min(2).max(20).nullable().default(null).describe("KVKK: araç plakası"),
    customerProfileId: uuidSchema.nullable().default(null),
    contactName: z.string().trim().min(2).max(120).nullable().default(null).describe("KVKK: ad soyad"),
    contactPhone: z
        .string()
        .trim()
        .min(7)
        .max(30)
        .nullable()
        .default(null)
        .describe("KVKK: telefon (E.164'e çevrilir)"),
    note: z.string().trim().max(500).nullable().default(null),
})
    .superRefine((value, context) => {
    if (value.customerProfileId === null && value.contactName === null && value.contactPhone === null)
        context.addIssue({
            code: "custom",
            path: ["contactPhone"],
            message: "Randevu için müşteri kaydı ya da iletişim bilgisi (ad / telefon) gereklidir",
        });
});
export const cancelWashAppointmentSchema = z.object({
    reason: z.string().trim().min(3).max(500),
    version: optionalVersion,
});
export const markWashAppointmentNoShowSchema = z.object({ version: optionalVersion });
export const checkInWashAppointmentSchema = z.object({
    staffId: uuidSchema.nullable().default(null).describe("İşi üstlenen personel (`station_staff`)"),
    version: optionalVersion,
});
export const washUnitParamsSchema = z.object({ unitId: uuidSchema });
export const washAvailabilityQuerySchema = z.object({
    date: localDateSchema.describe("İstanbul günü"),
    programIds: z
        .string()
        .describe("Virgülle ayrılmış HAND_WASH program kimlikleri (en çok 10)")
        .transform((value) => value.split(",").map((id) => id.trim()))
        .pipe(programIdsSchema),
});
export const washOpeningWindowSchema = z.object({
    weekdays: z.array(z.number().int().min(1).max(7)).describe("ISO hafta günleri; boş = her gün"),
    from: z.string().describe("HH:MM (İstanbul)"),
    to: z.string().describe("HH:MM (İstanbul, hariç)"),
});
export const washSlotSchema = z.object({
    startsAt: timestampSchema,
    endsAt: timestampSchema.describe("Slotun sonu (slot süresi); hizmetin sonu değil"),
    free: z.number().int().describe("Bu slotta başlayan randevu için boş kapasite"),
    bookable: z.boolean(),
});
export const washAvailabilitySchema = z.object({
    unitId: uuidSchema,
    unitName: z.string(),
    date: localDateSchema,
    capacity: z.number().int(),
    slotMinutes: z.number().int(),
    durationMinutes: z
        .number()
        .int()
        .describe("Seçilen hizmetlerin toplam süresi (dakikaya yukarı yuvarlanmış)"),
    openingHours: z.array(washOpeningWindowSchema),
    slots: z.array(washSlotSchema),
});
//# sourceMappingURL=appointments.js.map